import MembershipApi, {
    GetCheckoutUrlResponseShape,
    getCheckoutUrlDataShape,
} from '_services/Api/Membership/MembershipApi';
import Subscription from '_services/LocalStorage/Subscription';
import { flashMessageSlice } from '_services/Redux/FlashMessage/FlashMessageSlice';
import store, { dispatch } from '_services/Redux/store';
import { trialDuration as fixedTrialDuration } from 'Components/Shopify/Utils';
import { useSubscriptionCurrency } from 'Hooks/useSubscriptionCurrency';
import { useCallback, useEffect, useRef } from 'react';
import {
    CHECKOUT_META_EVENT_ID_STORAGE_KEY as _CHECKOUT_META_EVENT_ID_STORAGE_KEY,
    CHECKOUT_META_TRIAL_START_PARAM as _CHECKOUT_META_TRIAL_START_PARAM,
    consumeCheckoutMetaEventId as _consumeCheckoutMetaEventId,
    consumeCheckoutMetaPixelPayload as _consumeCheckoutMetaPixelPayload,
    consumeCheckoutUserEmail as _consumeCheckoutUserEmail,
    persistCheckoutMetaEventId as _persistCheckoutMetaEventId,
    buildCheckoutMetaPixelPayload,
    generateEventId,
    getCookieValue,
    getMetaPixelIds,
    getUrlParam,
    hasStoredCheckoutMetaPixelPayload,
    persistCheckoutMetaPixelPayload,
    persistCheckoutUserEmail,
    withTrialStartParam,
} from './useStripeCheckout.helpers';

export const CHECKOUT_META_EVENT_ID_STORAGE_KEY =
    _CHECKOUT_META_EVENT_ID_STORAGE_KEY;
export const CHECKOUT_META_TRIAL_START_PARAM = _CHECKOUT_META_TRIAL_START_PARAM;
export const persistCheckoutMetaEventId = _persistCheckoutMetaEventId;
export const consumeCheckoutMetaEventId = _consumeCheckoutMetaEventId;
export const consumeCheckoutMetaPixelPayload = _consumeCheckoutMetaPixelPayload;
export const consumeCheckoutUserEmail = _consumeCheckoutUserEmail;
export { hasStoredCheckoutMetaPixelPayload };

export const getFirstPromoterTid = (): string | null => {
    if (typeof window !== 'undefined') {
        const tid = (window as { FPROM?: { data?: { tid?: unknown } } }).FPROM
            ?.data?.tid;
        if (typeof tid === 'string' && tid.length > 0) {
            return tid;
        }
    }

    return getCookieValue('_fprom_tid');
};

const UTM_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
] as const;

type UTMKey = (typeof UTM_KEYS)[number];
type UTMParams = Partial<Record<UTMKey, string>>;

const normalizeUtmValue = (value: unknown): string | null => {
    if (typeof value !== 'string') {
        return null;
    }

    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
};

export const getUtmParams = (): UTMParams => {
    if (typeof window === 'undefined') {
        return {};
    }

    let stored: Record<string, unknown> | null = null;
    try {
        const raw = window.sessionStorage.getItem('utmParams');
        if (raw) {
            const parsed = JSON.parse(raw);
            if (
                parsed &&
                typeof parsed === 'object' &&
                !Array.isArray(parsed)
            ) {
                stored = parsed as Record<string, unknown>;
            }
        }
    } catch {
        stored = null;
    }

    const result: UTMParams = {};
    for (const key of UTM_KEYS) {
        const fromStorage = stored ? stored[key] : null;
        const storageValue = normalizeUtmValue(fromStorage);
        const urlValue = storageValue ?? normalizeUtmValue(getUrlParam(key));
        if (urlValue) {
            result[key] = urlValue;
        }
    }

    return result;
};

export interface CheckoutStripeProps extends getCheckoutUrlDataShape {
    onDone?: () => void;
    designerCode?: string;
    isLoading?: (isLoading: boolean) => void;
}

type CheckoutPrefetchOptions = {
    cacheKey?: string;
    delayMs?: number;
};

type CheckoutWithPrefetchProps = CheckoutStripeProps & {
    cacheKey?: string;
};

type StripeCheckoutProps = {
    onCheckoutButtonClick: (props: CheckoutStripeProps) => void;
    prefetchCheckoutUrl: (
        props: CheckoutStripeProps,
        options?: CheckoutPrefetchOptions,
    ) => void;
    onCheckoutButtonClickWithPrefetch: (
        props: CheckoutWithPrefetchProps,
    ) => Promise<void>;
};

/** Two ways to use. Either prefetch url first (async, takes a bit of time), then onCheckoutButtonClickWithPrefetch
 * Or onCheckoutButtonClick directly. (older and slower)
 */
const useStripeCheckout = (): StripeCheckoutProps => {
    const { sayError } = flashMessageSlice.actions;
    const normalizedCurrency = useSubscriptionCurrency();
    const prefetchedUrlByKeyRef = useRef<
        Map<string, GetCheckoutUrlResponseShape>
    >(new Map());
    const inFlightRequestByKeyRef = useRef<
        Map<string, Promise<GetCheckoutUrlResponseShape | null>>
    >(new Map());
    const prefetchTimerByKeyRef = useRef<
        Map<string, ReturnType<typeof setTimeout>>
    >(new Map());

    const getCheckoutCacheKey = useCallback(
        (props: CheckoutStripeProps, explicitCacheKey?: string): string => {
            if (explicitCacheKey) {
                return explicitCacheKey;
            }

            const {
                designerCode,
                product_id = 'premium_library',
                interval = 'month',
                trial = false,
                trialDuration = trial ? fixedTrialDuration : undefined,
                discount = false,
                success_url = '',
                return_url = '',
            } = props;

            return JSON.stringify({
                designerCode: designerCode ?? '',
                product_id,
                interval,
                trial,
                trialDuration: trialDuration ?? '',
                discount: discount ?? '',
                success_url,
                return_url,
                currency: normalizedCurrency,
            });
        },
        [normalizedCurrency],
    );

    const getStripeUrlAsync = useCallback(
        async ({
            designerCode,
            product_id = 'premium_library', // giving default as Premium
            interval = 'month',
            trial = false,
            trialDuration = trial ? fixedTrialDuration : undefined,
            discount = false,
            success_url,
            return_url,
        }: CheckoutStripeProps): Promise<GetCheckoutUrlResponseShape | null> => {
            const { fbp, fbc } = getMetaPixelIds();
            const event_id = generateEventId();
            const client_ip = undefined; // Let backend infer IP if needed
            const user_agent =
                typeof navigator === 'undefined'
                    ? undefined
                    : (navigator.userAgent ?? navigator.vendor ?? undefined);
            const referer =
                typeof document === 'undefined'
                    ? undefined
                    : document.referrer || undefined;

            const fp_tid = getFirstPromoterTid();
            const utmParams = getUtmParams();
            const hasUtmParams = Object.keys(utmParams).length > 0;
            const checkoutData: getCheckoutUrlDataShape & { fp_tid?: string } =
                {
                    product_id,
                    interval,
                    return_url: return_url || window.location.href,
                    trial,
                    trialDuration,
                    success_url: trial
                        ? withTrialStartParam(success_url)
                        : success_url,
                    discount,
                    currency: normalizedCurrency,
                    ...(fbp ? { fbp } : {}),
                    ...(fbc ? { fbc } : {}),
                    ...(event_id ? { event_id } : {}),
                    ...(fp_tid ? { fp_tid } : {}),
                    ...(client_ip ? { client_ip } : {}),
                    ...(user_agent ? { user_agent } : {}),
                    ...(referer ? { referer } : {}),
                    ...(hasUtmParams ? { utmParams } : {}),
                };

            if (designerCode) {
                checkoutData['designer_code'] = designerCode;
            }

            try {
                const response =
                    await MembershipApi.getCheckoutUrl(checkoutData);
                const { url, meta_event_id } = response.data;

                if (url) {
                    Subscription.set(
                        JSON.stringify({ type: product_id, interval }),
                    );
                    return { url, meta_event_id };
                }

                dispatch(
                    sayError(
                        'Something went wrong with Stripe. No url is generated',
                    ),
                );
                return null;
            } catch (error) {
                const errorMessage =
                    error instanceof Error ? error.message : String(error);
                console.error(errorMessage);

                dispatch(
                    sayError(
                        'Something went wrong with Stripe. Please try again later. Error:' +
                            errorMessage,
                    ),
                );
                return null;
            }
        },
        [normalizedCurrency, sayError],
    );

    const clearPrefetchTimer = useCallback((cacheKey: string): void => {
        const timerId = prefetchTimerByKeyRef.current.get(cacheKey);
        if (!timerId) {
            return;
        }
        clearTimeout(timerId);
        prefetchTimerByKeyRef.current.delete(cacheKey);
    }, []);

    const getOrCreateRequest = useCallback(
        (cacheKey: string, checkoutProps: CheckoutStripeProps) => {
            const activeRequest = inFlightRequestByKeyRef.current.get(cacheKey);
            if (activeRequest) {
                return activeRequest;
            }

            const request = getStripeUrlAsync(checkoutProps).finally(() => {
                inFlightRequestByKeyRef.current.delete(cacheKey);
            });

            inFlightRequestByKeyRef.current.set(cacheKey, request);
            return request;
        },
        [getStripeUrlAsync],
    );

    const prefetchCheckoutUrl = useCallback(
        (
            checkoutProps: CheckoutStripeProps,
            options?: CheckoutPrefetchOptions,
        ): void => {
            const cacheKey = getCheckoutCacheKey(
                checkoutProps,
                options?.cacheKey,
            );
            const delayMs = options?.delayMs ?? 1000;

            clearPrefetchTimer(cacheKey);
            if (prefetchedUrlByKeyRef.current.has(cacheKey)) {
                return;
            }

            const timerId = setTimeout(() => {
                void getOrCreateRequest(cacheKey, checkoutProps).then(
                    (checkoutResponse) => {
                        if (!checkoutResponse?.url) {
                            return;
                        }
                        prefetchedUrlByKeyRef.current.set(
                            cacheKey,
                            checkoutResponse,
                        );
                    },
                );
                prefetchTimerByKeyRef.current.delete(cacheKey);
            }, delayMs);

            prefetchTimerByKeyRef.current.set(cacheKey, timerId);
        },
        [clearPrefetchTimer, getCheckoutCacheKey, getOrCreateRequest],
    );

    const onCheckoutButtonClickWithPrefetch = useCallback(
        async ({
            onDone,
            isLoading,
            cacheKey: explicitCacheKey,
            ...checkoutProps
        }: CheckoutWithPrefetchProps): Promise<void> => {
            isLoading?.(true);

            const cacheKey = getCheckoutCacheKey(
                checkoutProps,
                explicitCacheKey,
            );
            clearPrefetchTimer(cacheKey);

            try {
                let checkoutResponse: GetCheckoutUrlResponseShape | null = null;
                checkoutResponse =
                    prefetchedUrlByKeyRef.current.get(cacheKey) ?? null;

                if (!checkoutResponse) {
                    checkoutResponse = await getOrCreateRequest(
                        cacheKey,
                        checkoutProps,
                    );
                    if (checkoutResponse?.url) {
                        prefetchedUrlByKeyRef.current.set(
                            cacheKey,
                            checkoutResponse,
                        );
                    }
                }

                if (!checkoutResponse?.url) {
                    return;
                }

                persistCheckoutMetaEventId(checkoutResponse.meta_event_id);
                persistCheckoutMetaPixelPayload(
                    checkoutProps.trial
                        ? buildCheckoutMetaPixelPayload({
                              product_id: checkoutProps.product_id,
                              interval: checkoutProps.interval,
                              currency: normalizedCurrency,
                          })
                        : null,
                );
                persistCheckoutUserEmail(store.getState().user.data?.email);

                window.location.href = checkoutResponse.url;
            } finally {
                isLoading?.(false);
                onDone?.();
            }
        },
        [
            clearPrefetchTimer,
            getCheckoutCacheKey,
            getOrCreateRequest,
            normalizedCurrency,
        ],
    );

    useEffect(() => {
        return () => {
            prefetchTimerByKeyRef.current.forEach((timerId) => {
                clearTimeout(timerId);
            });
            prefetchTimerByKeyRef.current.clear();
        };
    }, []);

    /**
     * Handled subscription options
     */
    const onCheckoutButtonClick = useCallback(
        (props: CheckoutStripeProps) => {
            void onCheckoutButtonClickWithPrefetch(props);
        },
        [onCheckoutButtonClickWithPrefetch],
    );

    return {
        onCheckoutButtonClick,
        prefetchCheckoutUrl,
        onCheckoutButtonClickWithPrefetch,
    };
};

export default useStripeCheckout;
